import { i18n } from '@lingui/core';
import { useLingui } from '@lingui/react/macro';
import { BASE_URL } from '@/base/constants';

const ZENDESK_SNIPPET_URL = `https://static.zdassets.com/ekr/snippet.js?key=${import.meta.env.VITE_ZENDESK_KEY}`;

let zendeskLoadPromise: Promise<void> | null = null;

function loadZendeskScript(): Promise<void> {
  if ((window as Window & { zE?: unknown }).zE) return Promise.resolve();
  if (zendeskLoadPromise) return zendeskLoadPromise;
  zendeskLoadPromise = new Promise((resolve) => {
    const s = document.createElement('script');
    s.src = ZENDESK_SNIPPET_URL;
    s.id = 'ze-snippet';
    s.onload = () => resolve();
    s.onerror = () => resolve();
    (document.body ?? document.head).appendChild(s);
  });
  return zendeskLoadPromise;
}

const zendesk = (() => {
  let lastValue = false;
  let initialized = false;

  const call = (method: string) => {
    (window as Window & { zE?: (...args: unknown[]) => void }).zE?.(
      'messenger',
      method,
    );
  };
  const on = (evt: string, handler: (evt: unknown) => void) => {
    (window as Window & { zE?: (...args: unknown[]) => void }).zE?.(
      'messenger:on',
      evt,
      handler,
    );
  };
  const set = (key: string, value: unknown) => {
    (window as Window & { zE?: (...args: unknown[]) => void }).zE?.(
      'messenger:set',
      key,
      value,
    );
  };

  const toggle = (newValue?: boolean) =>
    call((newValue ?? !lastValue) ? 'open' : 'close');

  const close = () => call('close');

  function init() {
    if (
      initialized ||
      !(
        typeof window !== 'undefined' &&
        (window as Window & { zE?: unknown }).zE
      )
    )
      return;
    initialized = true;
    on('open', () => {
      lastValue = true;
      setTimeout(() => window.addEventListener('click', close), 100);
    });
    on('close', () => {
      lastValue = false;
      window.removeEventListener('click', close);
    });
    set('locale', i18n.locale);
    close();
  }

  i18n.on('change', () => set('locale', i18n.locale));

  return { toggle, init };
})();

async function openSupport() {
  if (typeof window === 'undefined') return;
  const w = window as Window & { zE?: unknown };
  if (!w.zE) {
    await loadZendeskScript();
    zendesk.init();
    zendesk.toggle(true);
  } else {
    zendesk.init();
    zendesk.toggle();
  }
}

export const useExternalLinks = () => {
  const { i18n } = useLingui();
  return {
    support: () => void openSupport(),
    supportEmail: 'support@iqx.io',
    helpCenter: `${BASE_URL}/${i18n.locale}/guide`,
    faq: `${BASE_URL}/${i18n.locale}/faq`,
    privacyPolicy: `${BASE_URL}/${i18n.locale}/privacy-policy`,
    termsOfUse: `${BASE_URL}/${i18n.locale}/terms-of-use`,
    telegram: 'https://t.me/iqxexchange',
    linkedin: 'https://linkedin.com',
    instagram: 'https://instagram.com/iqxexchange',
    twitter: 'https://x.com/IQX_Exchange',
    authenticatorAndroid:
      'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&pli=1',
    authenticatorIos:
      'https://apps.apple.com/us/app/google-authenticator/id388497605',
    blog: `${BASE_URL}/blog`,
  };
};
